import {
  WSActions,
  WSCard,
  WSControlGroup,
  WSDivider,
  WSForm,
  WSList,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import { TaxFilingCode } from "@wingspanhq/users/dist/lib/interfaces";
import { useWSMutation } from "@ws-react-query";
import * as Yup from "yup";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import {
  FILLING_CODE_OPTIONS,
  US_STATES_OPTIONS
} from "../../../constants/user";
import { useUserId } from "../../../query/hooks/helpers";
import { QUERY_USERS_MEMBER_PROFILE } from "../../../query/users/keys";
import { useQueryMemberProfile } from "../../../query/users/queries";
import { getIncomeOptionsByFilingCode } from "../../../query/users/selectors";
import { usersService } from "../../../services/users";
import { WSServiceError } from "../../../utils/serviceHelper";
import { useModalHowWeCalculateTaxRate } from "../modals/ModalHowWeCalculateTaxRate";

type FormData = {
  filingCode: TaxFilingCode;
  expectedIncome: string;
  stateOfResidence: string;
};

type Props = {
  onBack?: () => void;
  onNext?: () => void;
  isEdit?: boolean;
};

export const FormTaxProfile: React.FC<Props> = props => {
  const userId = useUserId();
  const queryMember = useQueryMemberProfile(userId);
  const modalHowWeCalculateTaxRate = useModalHowWeCalculateTaxRate();

  const [submit, submitMeta] = useWSMutation<void, WSServiceError, FormData>(
    async data => {
      await usersService.member.update(userId, {
        memberId: userId,
        profile: {
          taxInfo: {
            filingCode: data.filingCode,
            incomeEstimateForYear: Number(data.expectedIncome),
            stateOfResidence: data.stateOfResidence
          }
        }
      });
    },
    {
      dependencies: [QUERY_USERS_MEMBER_PROFILE],
      onSuccess: props.onNext
    }
  );

  return (
    <WSForm<FormData>
      defaultValues={{
        filingCode: queryMember.data?.profile.taxInfo?.filingCode,
        expectedIncome: String(
          queryMember.data?.profile.taxInfo?.incomeEstimateForYear
        ),
        stateOfResidence:
          queryMember.data?.profile.taxInfo?.stateOfResidence ||
          queryMember.data?.profile.address?.state
      }}
      validationSchema={Yup.object().shape({
        filingCode: Yup.string().nullable().required("Required"),
        expectedIncome: Yup.string().nullable().required("Required"),
        stateOfResidence: Yup.string().nullable().required("Required")
      })}
      onSubmit={submit}
    >
      <WSList gap="2XL">
        <WSCard
          header={
            props.isEdit
              ? "Your tax profile"
              : "Calculate your estimated tax rate"
          }
        >
          {!props.isEdit && (
            <>
              <WSText.ParagraphSm color="gray500">
                Let’s determine how much you should set aside.{" "}
                <a
                  onClick={() => {
                    modalHowWeCalculateTaxRate.open();
                  }}
                >
                  Learn more
                </a>{" "}
                about your rate calculation.
              </WSText.ParagraphSm>

              <WSDivider my="L" />
            </>
          )}

          <WSList gap="2XL">
            <WSForm.Field
              name="filingCode"
              component={WSSelect}
              componentProps={{
                label: "What’s your filing status?",
                required: true,
                helperText:
                  "We suggest selecting the same filing status as last year unless you’ve had a major life event.",
                options: FILLING_CODE_OPTIONS
              }}
            />

            <WSForm.Value name="filingCode">
              {filingCode => {
                const options = getIncomeOptionsByFilingCode(filingCode).map(
                  option => ({
                    label: option.label,
                    value: String(option.value)
                  })
                );

                return (
                  <WSForm.Field
                    name="expectedIncome"
                    component={WSControlGroup}
                    componentProps={{
                      type: "radio",
                      label: "How much do you expect to make this year?",
                      required: true,
                      helperText:
                        "Take your best guess. You can always change this estimate later.",
                      options: options
                    }}
                  />
                );
              }}
            </WSForm.Value>

            <WSForm.Field
              name="stateOfResidence"
              component={WSSelect}
              componentProps={{
                label: "What’s your State of residence?",
                required: true,
                helperText:
                  "We’ll factor both Federal and State estimates to increase the accuracy of our recommendation.",
                internalSearch: true,
                options: US_STATES_OPTIONS
              }}
            />

            <WSErrorMessage contextKey="TaxFilling" error={submitMeta.error} />
          </WSList>
        </WSCard>

        <WSActions
          alignment="fill"
          buttons={[
            {
              type: "button",
              kind: "Tertiary",
              label: "Back",
              onClick: props.onBack,
              visible: !!props.onBack
            },
            {
              label: props.isEdit ? "Continue" : "Calculate my rate",
              loading: submitMeta.isLoading
            }
          ]}
        />
      </WSList>
    </WSForm>
  );
};
